<template>
  <div class="animated fadeIn">
    <div class="row">
      <div class="col-xl">
        <b-card>
          <template slot="header">
            <survey-filter v-model="surveyFilter" :disabled="isExporting" :multipleSurveySelection="false" @input="loadData" />
          </template>

          <!-- Export type Radio-Selector -->
          <b-form-group v-if="isMSMUser"
                        :label-cols-sm="2" :label="$t('Webportal.ExportType')" label-class="font-weight-bold pt-0">
            <b-form-radio-group buttons button-variant="outline-secondary"
                                v-model="exportTypeValue"
                                :disabled="noSurveySelected || isExporting">
              <b-form-radio :value="ExportType.Customer">{{ $t('Webportal.Customer') }}</b-form-radio>
              <b-form-radio :value="ExportType.QualityAssurance">{{ $t('Webportal.QualityAssurance') }}</b-form-radio>
              <b-form-radio :value="ExportType.Points">{{ $t('Webportal.Points') }}</b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <!-- Target group selection -->
          <b-form-group v-if="isMSMUser && exportTypeCustomerSelected"
                        :label-cols-sm="2" :label="$t('Webportal.TargetGroup')" label-class="font-weight-bold"
                        :disabled="noSurveySelected || targetGroups.length == 0 || isExporting || !this.exportTypeCustomerSelected">
            <b-form-select class="col-xl-2 col-lg-2 col-md-3 col-sm-4" v-model="targetGroupValue">
                <option v-for="targetGroup in targetGroups" :key="targetGroup.TargetGroup" :value="targetGroup.TargetGroup">
                  {{ $t('Webportal.TargetGroup') }} {{ targetGroup.TargetGroup }} ({{ targetGroup.GroupNames.join(' / ') }})
                </option>
            </b-form-select>
          </b-form-group>

          <!-- Customer Status selection -->
          <b-form-group v-if="isMSMUser && exportTypeCustomerSelected"
                        :label-cols-sm="2" :label="$t('Webportal.Status')" label-class="font-weight-bold"
                        :disabled="noSurveySelected || isExporting || !this.exportTypeCustomerSelected">
            <b-form-group>
              <div class="form-row">
                <div class="col-auto">
                  <b-form-input v-model="statusMinValue"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
                <div class="col-auto">
                  <label class="col-form-label">{{ $t('Webportal.RangeTo') }}</label>
                </div>
                <div class="col-auto">
                  <b-form-input v-model="statusMaxValue"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
              </div>
            </b-form-group>
            <b-form-group>
              <b-form-checkbox v-model="includeTestchecks"
                              :disabled="noSurveySelected || isExporting">
                              {{ $t('Webportal.Include') }} {{ $t('Webportal.IncludeTestchecks') }}</b-form-checkbox>
            </b-form-group>
          </b-form-group>

          <!-- QA Status selection -->
          <b-form-group v-if="isMSMUser && exportTypeQASelected"
                        :label-cols-sm="2" :label="$t('Webportal.Status')" label-class="font-weight-bold"
                        :disabled="noSurveySelected || isExporting || !this.exportTypeQASelected">
            <b-form-group>
              <div class="form-row">
                <div class="col-auto">
                  <b-form-input v-model="statusMinValueQA"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
                <div class="col-auto">
                  <label class="col-form-label">{{ $t('Webportal.RangeTo') }}</label>
                </div>
                <div class="col-auto">
                  <b-form-input v-model="statusMaxValueQA"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
              </div>
            </b-form-group>
            <b-form-group>
              <b-form-checkbox v-model="includeTestchecks"
                              :disabled="noSurveySelected || isExporting">
                              {{ $t('Webportal.Include') }} {{ $t('Webportal.IncludeTestchecks') }}</b-form-checkbox>
            </b-form-group>
          </b-form-group>

          <!-- Points Status selection -->
          <b-form-group v-if="isMSMUser && exportTypePointsSelected"
                        :label-cols-sm="2" :label="$t('Webportal.Status')" label-class="font-weight-bold"
                        :disabled="noSurveySelected || isExporting || !this.exportTypePointsSelected">
            <b-form-group>
              <div class="form-row">
                <div class="col-auto">
                  <b-form-input v-model="statusMinValuePoints"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
                <div class="col-auto">
                  <label class="col-form-label">{{ $t('Webportal.RangeTo') }}</label>
                </div>
                <div class="col-auto">
                  <b-form-input v-model="statusMaxValuePoints"
                                type="number"
                                min="0"
                                max="400"
                                style="max-width:5em"></b-form-input>
                </div>
              </div>
            </b-form-group>
            <b-form-group>
              <b-form-checkbox v-model="includeTestchecks"
                              :disabled="noSurveySelected || isExporting">
                              {{ $t('Webportal.Include') }} {{ $t('Webportal.IncludeTestchecks') }}</b-form-checkbox>
            </b-form-group>
          </b-form-group>

          <b-form-group v-if="exportTypeCustomerSelected || exportTypeQASelected"
                        :label-cols-sm="2" label-class="font-weight-bold">
            <template slot="label">
              <b-badge class="badge-highlight">&nbsp;&nbsp;</b-badge>
              {{ $t('Webportal.Kpis') }}
            </template>
            <b-form-group>
              <treeselect ref="kpiTreeselect"
                          v-model="kpiValue"
                          :auto-load-root-options="false"
                          :placeholder="$t('Webportal.KpiFilter.Placeholder')"
                          :options="kpiItems"
                          valueFormat="object"
                          :disabled="noSurveySelected || isExporting"
                          :disableFuzzyMatching="true"
                          :multiple="true"
                          :showCount="true"
                          showCountOf="LEAF_DESCENDANTS"
                          :defaultExpandLevel="1">
              </treeselect>
            </b-form-group>
            <b-form-checkbox v-model="includeKpiPoints" v-show="hasPointSelection"
                             :disabled="noSurveySelected || isExporting">
                             {{ $t('Webportal.Include') }} {{ $t('Webportal.KpiPoints') }}</b-form-checkbox>
          </b-form-group>

          <b-form-group v-if="exportTypeCustomerSelected || exportTypeQASelected" :label-cols-sm="2"
                        label-class="font-weight-bold pt-0">
            <template slot="label">
              <b-badge style="background-color: #6c757d;">&nbsp;&nbsp;</b-badge>
              {{ $t('Webportal.ClusterResults') }}
            </template>
            <b-form-checkbox inline v-model="includeClusterPercentages" :disabled="noSurveySelected || isExporting">
                            {{ $t('Webportal.Include') }} {{ $t('Webportal.ClusterResults') }}</b-form-checkbox>
            <b-form-checkbox inline v-show="hasPointSelection"
                             v-model="includeClusterPoints" :disabled="noSurveySelected || isExporting || !includeClusterPercentages">
                            {{ $t('Webportal.Include') }} {{ $t('Webportal.ClusterPoints') }}</b-form-checkbox>
          </b-form-group>

          <!-- Includes section -->
          <b-form-group :label-cols-sm="2" :label="$t('Webportal.Include')" label-class="font-weight-bold pt-0">
            <b-form-checkbox-group stacked
                                  v-model="includedDataValue"
                                  :disabled="noSurveySelected || isExporting">
              <b-form-checkbox v-show="exportTypePointsSelected" :value="IncludedData.CheckInfo">{{ $t('Webportal.CheckInfo') }}</b-form-checkbox>
              <b-form-checkbox :value="IncludedData.LocationInfo">{{ $t('Webportal.LocationInfo') }}</b-form-checkbox>
              <b-form-checkbox v-show="exportTypeQASelected || exportTypeCustomerSelected"
                              :value="IncludedData.Properties">{{ $t('Webportal.Properties') }}</b-form-checkbox>
              <b-form-checkbox v-show="exportTypeQASelected || exportTypeCustomerSelected"
                              :value="IncludedData.QuestionsWithoutAnswers">{{ $t('Webportal.QuestionsWithoutAnswers') }}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>

          <div class="form-row">
            <div class="col-md-auto">
              <b-alert :show="noDataAlert"
                      dismissible
                        @dismissed="noDataAlert=0"
                      variant="warning">
                {{ $t('Webportal.NoDataFound') }}
              </b-alert>
              <b-button variant="primary"
                        :disabled="noSurveySelected || isExporting"
                        @click="downloadRawData()">
                {{ $t('Webportal.Download') }}
                <font-awesome-icon icon="download" />
              </b-button>
            </div>
            <div class="col-md-1" v-bind:class="{ inlineSpinner: isExporting }"></div>
          </div>

        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import AppConfig from '@/mixins/AppConfig-APP_TARGET';
import DownloadFileMixin from '@/mixins/DownloadFileMixin';
import SurveyFilter from '@/components/SurveyFilter-APP_TARGET';

import AuthenticationService from '@/services/AuthenticationService';
import SurveysService from '@/services/SurveysService';
import ReportsService from '@/services/ReportsService';

const ExportType = {
  Customer: 1,
  QualityAssurance: 2,
  Points: 3,
}

const IncludedData = {
  QuestionsWithoutAnswers: 1,
  LocationInfo: 3,
  Properties: 4,
  KpiPoints: 5,
  CheckInfo: 6
}

export default {
  name: "RawData",

  mixins: [ AppConfig, DownloadFileMixin ],

  components: { SurveyFilter },

  data () {
    return {
      // Filter properties
      surveyFilter: [],
      currentFilter: null,                  // Current filter object for change detection

      targetGroups: [],
      targetGroupValue: null,

      statusMinValue: 220,                  // Customer Min/Max Values
      statusMaxValue: 220,
      statusMinValueQA: 100,                // Quality Assurance Min/Max Values
      statusMaxValueQA: 299,
      statusMinValuePoints: 220,            // Points Min/Max Values
      statusMaxValuePoints: 220,
      includeTestchecks: false,

      ExportType: ExportType,
      exportTypeValue: ExportType.Customer,
      includeKpiPoints: true,
      kpiItems: [],                        // Data items for kpi treeselect
      kpiValue: [],                        // Selected kpi items
      includeClusterPercentages: false,
      includeClusterPoints: false,
      IncludedData: IncludedData,
      includedDataValue: [
        IncludedData.LocationInfo,
        IncludedData.Properties,
        IncludedData.CheckInfo
      ],

      isExporting: false,
      noDataAlert: 0
    }
  },

  computed: {
    isMSMUser: function () {
      return AuthenticationService.isMSMUser;
    },

    hasPointSelection: function() {
      return this.hasRawDataPointSelection() || (this.exportTypeQASelected && this.isMSMUser);
    },

    noSurveySelected: function () {
      return _.isEmpty(this.surveyFilter.computedSurveyIds);
    },

    exportTypeCustomerSelected: function () {
      return this.exportTypeValue === ExportType.Customer;
    },

    exportTypeQASelected: function () {
      return this.exportTypeValue === ExportType.QualityAssurance;
    },

    exportTypePointsSelected: function () {
      return this.exportTypeValue === ExportType.Points;
    },

    getMinimumStatus: function() {
      if (!this.isMSMUser) {
        return null
      }
      if (this.exportTypeQASelected) {
        return this.statusMinValueQA
      }
      if (this.exportTypePointsSelected) {
        return this.statusMinValuePoints
      }
      if (this.exportTypeCustomerSelected) {
        return this.statusMinValue
      }
      return null
    },

    getMaximumStatus: function() {
     if (!this.isMSMUser) {
        return null
      }
      if (this.exportTypeCustomerSelected) {
        return this.statusMaxValue
      }
      if (this.exportTypeQASelected) {
        return this.statusMaxValueQA
      }
      if (this.exportTypePointsSelected) {
        return this.statusMaxValuePoints
      }
      return null
    },

    filter: function() {
      // Returns an array of all KPI ids recursively for the given node
      let returnKpiIds = (obj) => {
          return _.concat(obj.id && obj.id[0] === 'K' ? obj.id.substring(1) : [],
              obj.children ? _.flatMapDeep(obj.children, returnKpiIds) : []);
      };

      let result = {
        surveyIds: this.surveyFilter.computedSurveyIds,
        waveIds: this.surveyFilter.waveIds,
        scenarioIds: this.surveyFilter.scenarioIds,
        dateFrom: this.surveyFilter.dateFrom,
        dateTo: this.surveyFilter.dateTo,
        propertyIds: this.surveyFilter.propertyIds,
        targetGroup: this.exportTypeCustomerSelected ? this.targetGroupValue : null,
        kpiIds: !_.isEqual(this.kpiValue, this.kpiItems) ? _.flatMapDeep(this.kpiValue, returnKpiIds) : null,
        surveyInstanceStatusMinimum: this.getMinimumStatus,
        surveyInstanceStatusMaximum: this.getMaximumStatus,
        includeTestchecks: this.includeTestchecks
      };

      return result;
    },

    options: function() {
      let result = {
        isQualityAssurance: this.exportTypeValue !== ExportType.Customer,
        includeQuestionsWithoutAnswers: _.includes(this.includedDataValue, IncludedData.QuestionsWithoutAnswers),
        includeLocationInfo: _.includes(this.includedDataValue, IncludedData.LocationInfo),
        includeCheckInfo: _.includes(this.includedDataValue, IncludedData.CheckInfo),
        includeProperties: _.includes(this.includedDataValue, IncludedData.Properties),
        includeKpiPercentages: _.size(this.kpiValue) > 0,
        includeKpiPoints: this.hasPointSelection && this.includeKpiPoints && _.size(this.kpiValue) > 0,
        includeClusterPercentages: this.includeClusterPercentages,
        includeClusterPoints: this.hasPointSelection && this.includeClusterPercentages && this.includeClusterPoints,
        includeOverallPoints: this.hasPointSelection,
        propertyUseLogicalAnd: this.surveyFilter.propertyUseLogicalAnd
      }
      return result;
    }
  },

  watch: {
    exportTypeValue: function () {
       this.selectDefaultKpis();

       this.includedDataValue = [
        IncludedData.LocationInfo,
        IncludedData.Properties,
        IncludedData.CheckInfo
      ];
      if (this.exportTypeQASelected)
        this.includedDataValue.push(IncludedData.QuestionsWithoutAnswers);
    }
  },

  mounted() {
    this.initializeTheme();
  },

  methods: {
    getSurveyTargetGroups() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyTargetGroups(this.surveyFilter.computedSurveyIds)
          .then(response => {
            this.targetGroups = _(response.data)
              .groupBy(item => item.TargetGroup)
              .map((targetGroups) => ({
                TargetGroup: _.first(targetGroups).TargetGroup,
                GroupNames: _.uniq(_.map(targetGroups, item => item.GroupName))
              }))
              .orderBy(item => item.TargetGroup)
              .value();
              
            if (this.targetGroups.length > 0)
              this.targetGroupValue = this.targetGroups[0].TargetGroup;
          })
      };
    },

    selectDefaultKpis() {
      this.kpiValue = [];

      if (this.exportTypeCustomerSelected && !_.isEmpty(this.kpiItems))
        this.kpiValue.push(_.first(this.kpiItems));
    },

    // Get survey structure data for the structure treeselect
    getSurveyKpis() {
      if (!this.noSurveySelected)
      {
        SurveysService.getSurveyKpis({ surveyIds: [ this.surveyFilter.computedSurveyIds[0] ] })
          .then(response => {
            // if only one survey selected/available, hide it
            if (response.data && response.data.length === 1 && response.data[0].children) {
              this.kpiItems = response.data[0].children;
            } else {
              this.kpiItems = [];
            }

            // Preselect all root items
            setTimeout(() => {
              this.selectDefaultKpis();
              this.includeClusterPercentages = _.size(this.kpiItems) === 0;
              this.includeClusterPoints = false;
            }, 0)
          })
      };
    },

    // Called whenever the input of the survey filter changes
    loadData() {
      if (!_.isEqual(this.surveyFilter?.computedSurveyIds, this.currentFilter?.computedSurveyIds)) {
        this.currentFilter = this.surveyFilter;

        this.targetGroupValue = null;
        this.targetGroups = [];
        this.kpiValue = [];

        this.getSurveyTargetGroups();
        this.getSurveyKpis();
      }
    },

    downloadRawData() {
      this.isExporting = true;
      this.noDataAlert = 0;

      let reportPromise = this.exportTypePointsSelected ?
        ReportsService.getRawDataIdPoints(this.filter, this.options) :
        ReportsService.getRawData(this.filter, this.options);

      reportPromise.then(response => {
          this.isExporting = false;

          if (response.status === 204)
            this.noDataAlert = 5;
          else
            this.downloadFile(response);
        })
        .catch(_ => { this.isExporting = false; } );
    }
  }
};
</script>
